import { ReducerCreators, PayloadAction } from '@reduxjs/toolkit';
import { ChannelSettingsState } from './types';
import { GetProjectAlertEmails, UpdateProjectAlertEmails } from '@/connection/emails/project-alert-emails';
import { GetBccEmails, SetBccEmails } from '@/connection/emails/bcc-emails';
import {
  CreateChannelRedirect,
  DeleteChannelRedirect,
  GetChannelRedirects,
  UpdateChannelRedirect,
} from '@/connection/channel/redirects';
import { ChannelRedirect } from '@/micros/homepage/settings/settings-main/components/channel-redirects';
import { UpsertBccEmailsRow } from '@/types/email.types';
import { RootState } from '@/store/main';
import { CreateChannelApiKey, DeactivateChannelApiKey, GetChannelApiKeys } from '@/connection/channel/api-keys';
import {
  CreateSolutionInstance,
  FetchSolutions,
  GetChannelInstances,
  GetSolutionInstances,
  GetTrayUser,
  ToggleInstance,
} from '@/connection/integrations/integrations';
import { Channel } from '@/types/channel.types';
import { EMappedIntegrationTypes, ITrayUser, ITraySolution, ITraySolutionInstance } from '@/types/integrations.types';
import {
  CreateOauth2IntegrationForChannel,
  GetOauth2IntegrationForChannel,
  ToggleAdminUseOauth2Integration,
  ToggleOauthIntegration,
  UpdateOauth2Integration,
} from '@/connection/integrations/oauth';
import { Oauth2Integration, CreateOauth2Integration } from '@/types/registration.types';
import { GetStripeIntegration, SetStripeContact } from '@/connection/integrations/stripe';

export const reducers = (create: ReducerCreators<ChannelSettingsState>) => ({
  getProjectAlertEmails: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const results = await GetProjectAlertEmails({ channel });

        return results;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.projectAlertEmails = action.payload;
      },
    }
  ),
  updateProjectAlertEmails: create.asyncThunk(
    async ({ channel, emails }: { channel: number; emails: string[] }, { rejectWithValue }) => {
      try {
        const results = await UpdateProjectAlertEmails({ channel, emails });

        return results;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.projectAlertEmails = action.payload;
      },
    }
  ),
  updateBccEmails: create.asyncThunk(
    async ({ channel, emails }: { channel: number; emails: string[] }, { rejectWithValue, getState }) => {
      try {
        const state = getState() as RootState;
        const bccEmails = state.channelSettings.bccEmails;
        if (!bccEmails) return;

        const currentEmailsMap: { [key: string]: boolean } =
          bccEmails.reduce((acc, cur) => ({ ...acc, [cur.email]: true }), {}) || [];
        const newEmailsMap: { [key: string]: boolean } = emails.reduce((acc, cur) => ({ ...acc, [cur]: true }), {});

        const toDelete: UpsertBccEmailsRow[] = bccEmails
          .filter((email) => !newEmailsMap[email.email])
          .map((email) => ({ email: email.email, channel, enabled: false }));
        const toAdd: UpsertBccEmailsRow[] = emails
          .filter((email) => !currentEmailsMap[email])
          .map((email) => ({
            email,
            channel,
            enabled: true,
          }));

        const mappedEmails = [...toDelete, ...toAdd];

        const results = await SetBccEmails({ channel, emails: mappedEmails });

        return results;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (action.payload) {
          state.bccEmails = action.payload.filter((bccEmail) => bccEmail.enabled);
        }
      },
    }
  ),
  getRedirects: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const redirects = await GetChannelRedirects(channel);
        return redirects;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.redirects = action.payload;
      },
    }
  ),
  deleteRedirect: create.asyncThunk(
    async ({ redirect }: { redirect: ChannelRedirect }, { rejectWithValue }) => {
      try {
        const res = await DeleteChannelRedirect(redirect);
        return res;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.redirects) {
          state.redirects = state.redirects.filter((redirect) => redirect.id !== action.meta.arg.redirect.id);
        }
      },
    }
  ),
  updateRedirect: create.asyncThunk(
    async ({ redirect }: { redirect: ChannelRedirect }, { rejectWithValue }) => {
      try {
        const res = await UpdateChannelRedirect(redirect);
        return res;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.redirects) {
          state.redirects = state.redirects.map((redirect) =>
            redirect.id === action.payload.id ? action.payload : redirect
          );
        }
      },
    }
  ),
  createRedirect: create.asyncThunk(
    async ({ redirect }: { redirect: ChannelRedirect }, { rejectWithValue }) => {
      try {
        const res = await CreateChannelRedirect(redirect);
        return res;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.redirects) {
          state.redirects = state.redirects.concat(action.payload);
        }
      },
    }
  ),
  getBccEmails: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const bccEmails = await GetBccEmails({ channel });
        return bccEmails;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.bccEmails = action.payload.emails;
      },
    }
  ),
  setShowEditChannelDetailsModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showEditChannelDetailsModal = action.payload;
  }),
  setShowEditPrimaryContactModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showEditPrimaryContactModal = action.payload;
  }),
  setShowEditEmailSettingsModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showEditEmailSettingsModal = action.payload;
  }),
  setShowRedirectModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showRedirectModal = action.payload;
  }),
  getApiKeys: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const apiKeys = await GetChannelApiKeys(channel);
        return apiKeys;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      pending: (state) => {
        state.loadingApiKeys = true;
      },
      fulfilled: (state, action) => {
        state.apiKeys = action.payload;
      },
      settled: (state) => {
        state.loadingApiKeys = false;
      },
    }
  ),
  createApiKey: create.asyncThunk(async ({ channel }: { channel: number }, { rejectWithValue }) => {
    try {
      const apiKey = await CreateChannelApiKey(channel);
      return apiKey;
    } catch (error) {
      return rejectWithValue(error);
    }
  }),
  deactivateApiKey: create.asyncThunk(
    async ({ channel, keyId }: { channel: number; keyId: string }, { rejectWithValue }) => {
      try {
        const res = await DeactivateChannelApiKey(channel, keyId);
        return res;
      } catch (error) {
        return rejectWithValue(error);
      }
    }
  ),
  fetchChannelInstances: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const instances = await GetChannelInstances(channel);
        return instances;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.channelInstances = action.payload;
      },
    }
  ),
  fetchTrayUser: create.asyncThunk(
    async ({ channel }: { channel: Channel }, { rejectWithValue }) => {
      try {
        const instances = await GetTrayUser(channel.channel, channel.name, channel.uuid);
        return instances;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.trayUser = action.payload;
      },
    }
  ),
  fetchSolutions: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const instances = await FetchSolutions(channel);
        return instances;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.solutions = action.payload.solutions;
        state.solutionsMapped = action.payload.mappedSolutions;
      },
    }
  ),
  fetchSolutionInstances: create.asyncThunk(
    async ({ userId }: { userId: string }, { rejectWithValue }) => {
      try {
        const instances = await GetSolutionInstances(userId);
        return instances;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.solutionInstances = action.payload;
      },
    }
  ),
  toggleSolutionInstance: create.asyncThunk(
    async (
      {
        userId,
        channel,
        instanceId,
        enabled,
        type,
      }: { userId: string; channel: number; instanceId: string; enabled: boolean; type?: EMappedIntegrationTypes },
      { rejectWithValue }
    ) => {
      try {
        const instances = await ToggleInstance(userId, channel, instanceId, enabled, type);
        return instances;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.solutionInstances) {
          const updatedInstance = action.payload;

          state.solutionInstances = state.solutionInstances.map((instance) => {
            if (instance.id === updatedInstance.id) {
              return {
                ...instance,
                enabled: updatedInstance.enabled,
              };
            } else {
              return instance;
            }
          });
        }
      },
    }
  ),
  createSolutionInstance: create.asyncThunk(
    async (
      {
        channel,
        solutionId,
        solutionName,
        type,
        trayUser,
      }: {
        channel: number;
        solutionId: string;
        solutionName: string;
        type: EMappedIntegrationTypes;
        trayUser: ITrayUser;
      },
      { rejectWithValue }
    ) => {
      try {
        const instances = await CreateSolutionInstance(channel, solutionId, solutionName, type, trayUser);
        return instances;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        if (state.solutionInstances) {
          state.solutionInstances = [...state.solutionInstances, action.payload.instance];
        }
        state.trayUser = action.payload.user;
        state.instanceToCreate = action.payload.instance;
      },
    }
  ),
  clearInstanceToCreate: create.reducer((state) => {
    state.instanceToCreate = null;
  }),
  fetchOauthIntegration: create.asyncThunk(
    async ({ channel }: { channel: number }, { rejectWithValue }) => {
      try {
        const oauthIntegration = await GetOauth2IntegrationForChannel(channel);
        return oauthIntegration;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.oauthIntegration = action.payload || null;
      },
    }
  ),
  toggleOAuthIntegrationAdminUse: create.asyncThunk(
    async ({ channel, uuid, on }: { channel: number; uuid: string; on: boolean }, { rejectWithValue }) => {
      try {
        const isOn = await ToggleAdminUseOauth2Integration(channel, uuid, on);
        return isOn;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state) => {
        if (state.oauthIntegration) {
          state.oauthIntegration = { ...state.oauthIntegration, admin_login: !state.oauthIntegration.admin_login };
        }
      },
    }
  ),
  toggleOAuthIntegration: create.asyncThunk(
    async ({ channel, uuid, on }: { channel: number; uuid: string; on: boolean }, { rejectWithValue }) => {
      try {
        const isOn = await ToggleOauthIntegration(channel, uuid, on);
        return isOn;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state) => {
        if (state.oauthIntegration) {
          state.oauthIntegration = { ...state.oauthIntegration, active: !state.oauthIntegration.active };
        }
      },
    }
  ),
  createOAuthIntegration: create.asyncThunk(
    async (
      { channel, integration }: { channel: number; integration: CreateOauth2Integration },
      { rejectWithValue }
    ) => {
      try {
        const isOn = await CreateOauth2IntegrationForChannel(channel, integration);
        return isOn;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.oauthIntegration = action.payload;
      },
    }
  ),
  updateOAuthIntegration: create.asyncThunk(
    async ({ channel, integration }: { channel: number; integration: Oauth2Integration }, { rejectWithValue }) => {
      try {
        const isOn = await UpdateOauth2Integration(channel, integration);
        return isOn;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.oauthIntegration = action.payload;
      },
    }
  ),
  fetchStripeIntegration: create.asyncThunk(
    async (_, { rejectWithValue }) => {
      try {
        const stripeIntegration = await GetStripeIntegration();
        return stripeIntegration;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.stripe = {
          ...state.stripe,
          status: action.payload.stripe_status,
          contact: action.payload.primary_contact,
        };
      },
    }
  ),
  setStripeContact: create.asyncThunk(
    async ({ email }: { email: string }, { rejectWithValue }) => {
      try {
        const updatedEmail = await SetStripeContact(email);
        return updatedEmail;
      } catch (error) {
        return rejectWithValue(error);
      }
    },
    {
      fulfilled: (state, action) => {
        state.stripe = {
          ...state.stripe,
          contact: action.payload.email,
        };
      },
    }
  ),
  setShowStripeModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showStripeModal = action.payload;
  }),
  setShowUpsertIntegrationModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showUpsertIntegrationModal = action.payload;
  }),
  setShowSelectIntegrationModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showSelectIntegrationModal = action.payload;
  }),
  setIntegrationNameToAdd: create.reducer((state, action: PayloadAction<string | null>) => {
    state.integrationNameToAdd = action.payload;
  }),
  setShowHiveModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showHiveModal = action.payload;
  }),
  setShowKollectiveModal: create.reducer((state, action: PayloadAction<boolean>) => {
    state.showKollectiveModal = action.payload;
  }),
  setInboundIntegrationToEdit: create.reducer(
    (state, action: PayloadAction<{ solution: ITraySolution; instance: ITraySolutionInstance } | null>) => {
      state.inboundIntegrationToEdit = action.payload;
    }
  ),
});
